import { useEffect, useRef, useState } from 'react';
import { textoDeAviso } from '@/lib/avisos-analisis';
import { createPortal } from 'react-dom';
import { Check, Hand, Save, Sparkles, X } from 'lucide-react';
import { ICONOS_ACCION, ICONOS_ESTADO } from '@/config/iconografia';
import type { AnalysisConfidence, AnalysisItem, NutritionAnalysisResult } from '@/models/analysis';
import { PORTION_BUCKETS, bucketForGrams, kcalBand, macroDistribution } from '@/lib/porcion';
import { DataService, type FoodItem } from '@/services';

export interface AnalisisAplicado {
  nombre: string;
  porcionG: number;
  calorias: number;
  proteinas: number;
  carbohidratos: number;
  grasas: number;
}

interface AnalisisFotoResultadoProps {
  result: NutritionAnalysisResult;
  onApply: (applied: AnalisisAplicado) => void;
  onDiscard: () => void;
  /** Si se proporciona, muestra "Guardar" directo en vez de "Usar estos valores". */
  onSave?: () => void;
  /** Indica que el guardado automático está en curso; oculta "Guardar" y muestra spinner. */
  autoSaving?: boolean;
  /** Llamado justo antes de abrir el modal de edición (p. ej. para cancelar el auto-guardado). */
  onEditStart?: () => void;
  /** Reporta al padre los valores actuales (incluida la porción elegida) para que el
   *  guardado persista EXACTAMENTE lo que ve el usuario (evita el desajuste registro↔historial). */
  onValuesChange?: (v: AnalisisAplicado) => void;
}

// ── Utilidades de confianza ──────────────────────────────────────────────────

const CONF_STYLE: Record<AnalysisConfidence['level'], { label: string; bg: string; color: string }> = {
  high:   { label: 'Confianza alta',  bg: 'var(--green-soft)', color: 'var(--green-on-soft)' },
  medium: { label: 'Confianza media', bg: 'var(--amber-soft)', color: 'var(--amber)' },
  low:    { label: 'Confianza baja',  bg: 'var(--coral-soft)', color: 'var(--coral)' },
};

function ConfidencePill({ confidence, compact = false }: { confidence: AnalysisConfidence; compact?: boolean }) {
  const c = CONF_STYLE[confidence.level];
  return (
    <span style={{
      fontSize: compact ? 9 : 11, fontWeight: 700, padding: compact ? '2px 6px' : '3px 9px',
      borderRadius: 999, background: c.bg, color: c.color, flexShrink: 0, whiteSpace: 'nowrap',
    }}>
      {compact ? `${Math.round(confidence.score * 100)}%` : `${c.label} · ${Math.round(confidence.score * 100)}%`}
    </span>
  );
}

// ── Tipos internos ───────────────────────────────────────────────────────────

interface EditableItem {
  label: string;
  amount: number;
  unit: string;
  /** Valores por 100g si se seleccionó de la BD; null = mantener análisis original. */
  foodPer100g: { kcal: number; prot: number; carbs: number; fat: number } | null;
  /** Cantidad estimada por el análisis original (para reescalar las macros al cambiar amount). */
  origAmount: number;
  /** Macros del análisis original para fallback. */
  origKcal: number;
  origProt: number;
  origCarbs: number;
  origFat: number;
}

function calcMacros(it: EditableItem) {
  if (it.foodPer100g) {
    const f = it.amount / 100;
    return {
      kcal:  Math.round(it.foodPer100g.kcal  * f),
      prot:  Math.round(it.foodPer100g.prot  * f * 10) / 10,
      carbs: Math.round(it.foodPer100g.carbs * f * 10) / 10,
      fat:   Math.round(it.foodPer100g.fat   * f * 10) / 10,
    };
  }
  // Sin match de BD: escalar las macros del análisis original según el cambio de cantidad
  // (ej. de 100 g → 200 g duplica kcal). Si no hay cantidad original, no escala.
  const f = it.origAmount > 0 ? it.amount / it.origAmount : 1;
  return {
    kcal:  Math.round(it.origKcal  * f),
    prot:  Math.round(it.origProt  * f * 10) / 10,
    carbs: Math.round(it.origCarbs * f * 10) / 10,
    fat:   Math.round(it.origFat   * f * 10) / 10,
  };
}

// ── Fila editable con búsqueda de alimento ───────────────────────────────────

interface EditRowProps {
  item: EditableItem;
  canRemove: boolean;
  onChange: (patch: Partial<EditableItem>) => void;
  onRemove: () => void;
}

function EditRow({ item, canRemove, onChange, onRemove }: EditRowProps) {
  const [query, setQuery] = useState(item.label);
  const [results, setResults] = useState<FoodItem[]>([]);
  const [searching, setSearching] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = (q: string) => {
    setQuery(q);
    onChange({ label: q, foodPer100g: null }); // Reset BD match on manual edit
    if (debounce.current) clearTimeout(debounce.current);
    if (q.trim().length < 2) { setResults([]); return; }
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await DataService.buscarAlimentos(q);
        setResults(res);
      } finally {
        setSearching(false);
      }
    }, 350);
  };

  const selectFood = (food: FoodItem) => {
    setQuery(food.displayName ?? food.name);
    setResults([]);
    onChange({
      label: food.displayName ?? food.name,
      foodPer100g: {
        kcal:  food.kcalPer100g,
        prot:  food.proteinPer100g,
        carbs: food.carbsPer100g,
        fat:   food.fatPer100g,
      },
    });
  };

  const macros = calcMacros({ ...item, label: query });
  const hasBdMatch = item.foodPer100g !== null;

  return (
    <div className="bg-secondary/50 border border-border rounded-2xl p-3 space-y-2">

      {/* Nombre con búsqueda */}
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={query}
              onChange={e => search(e.target.value)}
              placeholder="Nombre del alimento"
              className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            {searching && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                buscando…
              </span>
            )}
          </div>
          {canRemove && (
            <button type="button" onClick={onRemove}
              aria-label="Quitar componente"
              className="w-8 h-8 rounded-full bg-destructive/10 text-destructive flex items-center justify-center flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Dropdown de resultados */}
        {results.length > 0 && (
          <ul className="absolute left-0 right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-lg z-10 overflow-hidden">
            {results.map(f => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => selectFood(f)}
                  className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-secondary/50 flex items-center justify-between gap-2"
                >
                  <span className="font-medium truncate">{f.displayName ?? f.name}</span>
                  <span className="text-[11px] text-muted-foreground flex-shrink-0">
                    {f.kcalPer100g} kcal/100g
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Cantidad + macros calculados */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground font-medium">Cantidad</span>
          <input
            type="number"
            value={item.amount}
            min={1}
            step={5}
            onChange={e => onChange({ amount: parseFloat(e.target.value) || 0 })}
            className="w-16 text-right rounded-lg border border-border bg-card px-2 py-1 text-xs font-semibold text-foreground focus:outline-none"
          />
          <span className="text-[11px] text-muted-foreground">{item.unit}</span>
        </label>

        {/* Preview de macros */}
        <div className="flex gap-2 ml-auto">
          {[
            { l: 'kcal',  v: macros.kcal },
            { l: 'P',     v: `${macros.prot}g` },
            { l: 'C',     v: `${macros.carbs}g` },
            { l: 'G',     v: `${macros.fat}g` },
          ].map(m => (
            <div key={m.l} className="text-center">
              <div className={`text-xs font-bold ${hasBdMatch ? 'text-primary' : 'text-muted-foreground'}`}>{m.v}</div>
              <div className="text-[9px] text-muted-foreground">{m.l}</div>
            </div>
          ))}
        </div>
      </div>

      {hasBdMatch && (
        <p className="text-[10px] text-primary font-medium flex items-center gap-1">
          <Check className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
          Valores recalculados desde la base de datos
        </p>
      )}
    </div>
  );
}

// ── Modal de corrección ──────────────────────────────────────────────────────

interface EditModalProps {
  items: EditableItem[];
  onApply: (items: EditableItem[]) => void;
  onCancel: () => void;
}

function EditModal({ items: initItems, onApply, onCancel }: EditModalProps) {
  const [items, setItems] = useState<EditableItem[]>(initItems.map(it => ({ ...it })));
  const [kcalTarget, setKcalTarget] = useState('');

  const update = (i: number, patch: Partial<EditableItem>) =>
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  const removeItem = (i: number) =>
    setItems(prev => prev.filter((_, idx) => idx !== i));

  const addItem = () =>
    setItems(prev => [...prev, {
      label: '', amount: 100, unit: 'g',
      foodPer100g: null,
      origAmount: 100,
      origKcal: 0, origProt: 0, origCarbs: 0, origFat: 0,
    }]);

  const currentTotal = items.reduce((acc, it) => acc + calcMacros(it).kcal, 0);

  const applyKcalTarget = () => {
    const target = parseFloat(kcalTarget);
    if (!target || target <= 0 || currentTotal <= 0) return;
    const factor = target / currentTotal;
    setItems(prev => prev.map(it => ({
      ...it,
      amount: Math.round(it.amount * factor * 10) / 10,
    })));
    setKcalTarget('');
  };

  return createPortal(
    <div className="ui-modern" style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'flex-end', background: 'rgba(0,0,0,.5)' }}>
      <div className="w-full overflow-y-auto bg-card rounded-t-3xl px-4 pb-10 pt-5" style={{ maxHeight: '88vh' }}>

        <div className="flex items-center mb-1">
          <p className="flex-1 text-[15px] font-bold text-foreground flex items-center gap-1.5">
            <ICONOS_ACCION.editar className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            Corregir componentes
          </p>
          <button type="button" onClick={onCancel}
            aria-label="Cerrar"
            className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-muted-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Ajuste rápido de kcal totales */}
        <div className="flex items-center gap-3 p-3 rounded-2xl bg-secondary/50 border border-border mb-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">IA estimó</div>
            <div className="text-base font-bold text-foreground">{currentTotal} kcal</div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[11px] text-muted-foreground whitespace-nowrap">Ajustar a</span>
            <input
              type="number"
              value={kcalTarget}
              min={1}
              onChange={e => setKcalTarget(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyKcalTarget()}
              placeholder="ej. 340"
              className="w-20 text-right rounded-lg border border-border bg-card px-2 py-1 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <span className="text-[11px] text-muted-foreground">kcal</span>
            <button
              type="button"
              onClick={applyKcalTarget}
              disabled={!kcalTarget || parseFloat(kcalTarget) <= 0}
              aria-label="Aplicar objetivo de kcal"
              className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
          Escribe el nombre correcto del alimento — la app buscará sus valores nutricionales reales.
        </p>

        <div className="space-y-3">
          {items.map((it, i) => (
            <EditRow
              key={i}
              item={it}
              canRemove={items.length > 1}
              onChange={patch => update(i, patch)}
              onRemove={() => removeItem(i)}
            />
          ))}
        </div>

        <button type="button" onClick={addItem}
          className="w-full mt-3 py-2.5 rounded-2xl border border-dashed border-border text-xs font-bold text-muted-foreground">
          + Añadir componente
        </button>

        <div className="flex gap-2 mt-5">
          <button type="button" onClick={() => onApply(items)}
            className="flex-1 py-3.5 rounded-2xl text-sm font-bold bg-primary text-primary-foreground">
            Aplicar corrección
          </button>
          <button type="button" onClick={onCancel}
            className="flex-shrink-0 px-4 rounded-2xl border border-border text-sm font-bold text-muted-foreground bg-card">
            Cancelar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export function AnalisisFotoResultado({ result, onApply, onDiscard, onSave, autoSaving, onEditStart, onValuesChange }: AnalisisFotoResultadoProps) {
  const { items, overallConfidence, warnings, notes } = result;
  const [editOpen, setEditOpen] = useState(false);
  const [correctedItems, setCorrectedItems] = useState<EditableItem[] | null>(null);
  /** Multiplicador de porción confirmado por el usuario (1 = estimación del modelo). */
  const [portionMul, setPortionMul] = useState(1);

  const toEditable = (it: AnalysisItem): EditableItem => ({
    label:      it.label,
    amount:     it.estimatedQuantity.amount,
    unit:       it.estimatedQuantity.unit,
    foodPer100g: null,
    origAmount: it.estimatedQuantity.amount,
    origKcal:  it.energyKcal,
    origProt:  it.macros.proteinG,
    origCarbs: it.macros.carbsG,
    origFat:   it.macros.fatG,
  });

  const displayItems = correctedItems ?? items.map(toEditable);

  const displayTotals = displayItems.reduce(
    (acc, it) => {
      const m = calcMacros(it);
      return { kcal: acc.kcal + m.kcal, prot: acc.prot + m.prot, carbs: acc.carbs + m.carbs, fat: acc.fat + m.fat };
    },
    { kcal: 0, prot: 0, carbs: 0, fat: 0 },
  );

  const hasCorrection = correctedItems !== null;

  // ── Presentación educativa: distribución fiable + banda de confianza + porción asistida ──
  const round1 = (n: number) => Math.round(n * 10) / 10;
  const estGrams = displayItems.reduce((s, it) => s + (it.unit === 'ud' ? 0 : it.amount), 0);
  const adjTotals = {
    kcal:  Math.round(displayTotals.kcal * portionMul),
    prot:  round1(displayTotals.prot * portionMul),
    carbs: round1(displayTotals.carbs * portionMul),
    fat:   round1(displayTotals.fat * portionMul),
  };
  const band = kcalBand(adjTotals.kcal, overallConfidence.level);
  const dist = macroDistribution(displayTotals.carbs, displayTotals.prot, displayTotals.fat);
  const currentBucket = bucketForGrams(estGrams > 0 ? estGrams * portionMul : 190);
  const setBucket = (typicalG: number) => setPortionMul(estGrams > 0 ? typicalG / estGrams : 1);

  // Reportar al padre los valores actuales (con la porción elegida) para que el auto-guardado
  // persista lo que el usuario ve, no el punto crudo. Se dispara al montar y al cambiar porción/edición.
  useEffect(() => {
    onValuesChange?.({
      nombre:        displayItems.map(it => it.label).join(' + '),
      porcionG:      Math.round((estGrams || 100) * portionMul),
      calorias:      adjTotals.kcal,
      proteinas:     adjTotals.prot,
      carbohidratos: adjTotals.carbs,
      grasas:        adjTotals.fat,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portionMul, correctedItems, result]);

  const apply = () => {
    onApply({
      nombre:        displayItems.map(it => it.label).join(' + '),
      porcionG:      Math.round((estGrams || 100) * portionMul),
      calorias:      adjTotals.kcal,
      proteinas:     adjTotals.prot,
      carbohidratos: adjTotals.carbs,
      grasas:        adjTotals.fat,
    });
  };

  const handleApplyEdit = (edited: EditableItem[]) => {
    setCorrectedItems(edited);
    setPortionMul(1); // la corrección redefine la base; la porción vuelve a la del análisis
    setEditOpen(false);
    // Notificar al padre inmediatamente (necesario en modo confirmación para actualizar el formulario)
    const totals = edited.reduce(
      (acc, it) => { const m = calcMacros(it); return { kcal: acc.kcal + m.kcal, prot: acc.prot + m.prot, carbs: acc.carbs + m.carbs, fat: acc.fat + m.fat }; },
      { kcal: 0, prot: 0, carbs: 0, fat: 0 },
    );
    onApply({
      nombre:        edited.map(it => it.label).join(' + '),
      porcionG:      edited.reduce((s, it) => s + (it.unit === 'ud' ? 0 : it.amount), 0) || 100,
      calorias:      totals.kcal,
      proteinas:     totals.prot,
      carbohidratos: totals.carbs,
      grasas:        totals.fat,
    });
  };

  // Reset corrección/porción si llega un nuevo resultado del análisis
  useEffect(() => { setCorrectedItems(null); setPortionMul(1); }, [result]);

  return (
    <>
      <div style={{
        background: 'var(--paper)', border: '1px solid var(--ink-100)',
        borderRadius: 'var(--r-lg)', padding: 16, marginBottom: 16,
        boxShadow: 'var(--shadow-card)',
      }}>
        {/* Cabecera */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Sparkles size={16} color="var(--primary)" style={{ flexShrink: 0 }} aria-hidden="true" />
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--navy)', flex: 1 }}>
            Análisis del plato
          </div>
          <ConfidencePill confidence={overallConfidence} />
        </div>

        {/* Items o versión corregida */}
        {hasCorrection ? (
          <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--green-soft)', border: '1px solid var(--green-deep)', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13, color: 'var(--green-on-soft)', marginBottom: 4 }}>
              <ICONOS_ESTADO.exito size={14} style={{ flexShrink: 0 }} aria-hidden="true" />
              <span>{correctedItems!.map(it => it.label).join(' + ')}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--green-on-soft)', opacity: 0.8 }}>
              {correctedItems!.some(it => it.foodPer100g) ? 'Valores recalculados desde BD' : 'Nombre corregido · valores del análisis original'}
            </div>
            <button type="button" onClick={() => setEditOpen(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--green-on-soft)', fontWeight: 700, padding: '4px 0 0', textDecoration: 'underline' }}>
              Volver a editar
            </button>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((it, i) => (
              <li key={`${it.label}-${i}`} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10, background: 'var(--ink-50)',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.label}
                    </span>
                    <ConfidencePill confidence={it.confidence} compact />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 3 }}>
                    {it.estimatedQuantity.amount} {it.estimatedQuantity.unit}
                    {' · '}P {it.macros.proteinG}g · C {it.macros.carbsG}g · G {it.macros.fatG}g
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--coral)' }}>{it.energyKcal}</div>
                  <div style={{ fontSize: 9, color: 'var(--ink-400)', fontWeight: 600 }}>kcal</div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Distribución de macros (señal fiable del modelo) */}
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: 'var(--ink-50)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>
            Cómo se reparte la energía
          </div>
          <div style={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', margin: '8px 0 7px' }}>
            <span style={{ width: `${dist.carbPct}%`, background: '#e0a83d' }} />
            <span style={{ width: `${dist.protPct}%`, background: 'var(--coral)' }} />
            <span style={{ width: `${dist.fatPct}%`, background: '#6f7dc9' }} />
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--ink-500)', fontWeight: 600, flexWrap: 'wrap' }}>
            <span>Hidratos {dist.carbPct}%</span><span>Proteína {dist.protPct}%</span><span>Grasa {dist.fatPct}%</span>
          </div>
        </div>

        {/* Energía como banda de confianza (no cifra exacta) */}
        <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 10, background: 'var(--navy)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,.6)' }}>
            Energía estimada · rango orientativo {band.label}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>{band.point}<span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,.7)' }}> kcal</span></div>
            <div style={{ display: 'flex', gap: 10, marginLeft: 'auto', fontSize: 12, color: 'rgba(255,255,255,.85)', fontWeight: 700 }}>
              <span>P {adjTotals.prot}g</span><span>C {adjTotals.carbs}g</span><span>G {adjTotals.fat}g</span>
            </div>
          </div>
        </div>

        {/* Selector de porción asistida (el paso educativo) */}
        <div style={{
          marginTop: 8, padding: '10px 12px', borderRadius: 10, background: 'var(--ink-50)',
          boxShadow: overallConfidence.level === 'high' ? undefined : '0 0 0 1.5px var(--amber)',
        }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--navy)' }}>¿Qué tamaño de porción?</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
            {PORTION_BUCKETS.map(b => {
              const on = b.id === currentBucket.id;
              return (
                <button key={b.id} type="button" onClick={() => setBucket(b.typicalG)}
                  style={{
                    textAlign: 'left', cursor: 'pointer', font: 'inherit', padding: 9, borderRadius: 12,
                    border: on ? '1.5px solid var(--coral)' : '1.5px solid var(--ink-100)',
                    background: on ? 'var(--coral-soft)' : 'var(--paper)',
                  }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--navy)' }}>{b.hand} {b.label}</div>
                  <div style={{ fontSize: 10, color: on ? 'var(--coral)' : 'var(--ink-400)', marginTop: 2, fontWeight: 600 }}>≈ {b.referencia}</div>
                </button>
              );
            })}
          </div>
          <p style={{ display: 'flex', gap: 6, fontSize: 10.5, color: 'var(--ink-500)', margin: '8px 0 0', lineHeight: 1.4 }}>
            <Hand size={13} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
            <span>El tamaño es lo más difícil de ver en una foto. Elígelo tú y de paso entrenas el ojo.</span>
          </p>
        </div>

        {/* Avisos */}
        {warnings.length > 0 && (
          <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {warnings.map((w, i) => (
              <li key={i} style={{ display: 'flex', gap: 6, fontSize: 11, color: 'var(--ink-500)', lineHeight: 1.35 }}>
                <ICONOS_ESTADO.advertencia size={12} color="var(--amber, #F7A400)" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" /><span>{textoDeAviso(w)}</span>
              </li>
            ))}
          </ul>
        )}

        {notes && (
          <p style={{ fontSize: 10.5, color: 'var(--ink-400)', margin: '10px 0 0', lineHeight: 1.4, fontStyle: 'italic' }}>
            {notes}
          </p>
        )}

        {/* Botones de acción */}
        <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
          {autoSaving ? (
            // Modo auto-guardado: solo spinner + Editar para cancelar
            <>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-400)' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)', opacity: 0.8, animation: 'pulse 1s infinite', flexShrink: 0 }} />
                Guardando automáticamente…
              </div>
              <button type="button" onClick={() => { onEditStart?.(); setEditOpen(true); }}
                style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--navy)', background: 'var(--paper)', color: 'var(--navy)', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <ICONOS_ACCION.editar size={13} aria-hidden="true" />
                Editar
              </button>
            </>
          ) : onSave ? (
            // Modo confirmación manual (auto-guardado cancelado): Guardar o Editar
            <>
              <button type="button" onClick={onSave} className="primary-btn" style={{ flex: 1, marginTop: 0, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {hasCorrection
                  ? <><ICONOS_ESTADO.exito size={14} aria-hidden="true" />Confirmar y guardar</>
                  : <><Save size={14} aria-hidden="true" />Guardar</>}
              </button>
              <button type="button" onClick={() => { onEditStart?.(); setEditOpen(true); }}
                style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--navy)', background: 'var(--paper)', color: 'var(--navy)', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <ICONOS_ACCION.editar size={13} aria-hidden="true" />
                Editar
              </button>
            </>
          ) : (
            // Modo revisión clásico: Usar / Editar / Descartar
            <>
              <button type="button" onClick={apply} className="primary-btn" style={{ flex: 1, marginTop: 0, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {hasCorrection
                  ? <><ICONOS_ESTADO.exito size={14} aria-hidden="true" />Usar corregido</>
                  : 'Usar estos valores'}
              </button>
              <button type="button" onClick={() => { onEditStart?.(); setEditOpen(true); }}
                style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--navy)', background: 'var(--paper)', color: 'var(--navy)', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <ICONOS_ACCION.editar size={13} aria-hidden="true" />
                Editar
              </button>
              <button type="button" onClick={onDiscard}
                style={{ flexShrink: 0, padding: '0 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--ink-100)', background: 'var(--paper)', color: 'var(--ink-500)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                Descartar
              </button>
            </>
          )}
        </div>

        <p style={{ fontSize: 10, color: 'var(--ink-400)', textAlign: 'center', margin: '8px 0 0', lineHeight: 1.4 }}>
          {autoSaving || onSave ? (
            <button type="button" onClick={onDiscard}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-400)', fontSize: 10, textDecoration: 'underline', padding: 0 }}>
              Ajustar manualmente
            </button>
          ) : 'Estimación orientativa generada por IA. Revisa y ajusta los valores antes de guardar.'}
        </p>
      </div>

      {editOpen && (
        <EditModal
          items={displayItems.map(it => ({ ...it }))}
          onApply={handleApplyEdit}
          onCancel={() => setEditOpen(false)}
        />
      )}
    </>
  );
}
